import apiClient from "./apiClient";
import {
  SubscriptionDetailsResponse,
  PaginatedListResponse,
  SubscriptionListItem,
  ChangeSubscriptionStatusDto,
  SuspendSubscriptionDto,
  ActivateSubscriptionDto,
  ExtendSubscriptionPeriodDto,
  RegisterBankTransferPaymentDto,
  RegisterBankTransferPaymentResponse,
  CreateManualSubscriptionDto,
  CreateManualSubscriptionResponse,
  StatusChangeHistoryItem,
  CreateBillingOperationDto,
  BillingOperationResponse,
  PaginatedBillingOperationsResponse,
  UpdateBillingPeriodDto,
  UpdateBillingPeriodResponse,
  SwitchToBankTransferDto,
  SwitchToBankTransferResponse,
  CreateAdminProformaDto,
  CreateAdminProformaResponse,
} from "../types/adminSubscriptions";

export const listAdminSubscriptions = async (query?: {
  status?: string;
  paymentSource?: string;
  brandId?: string;
  limit?: number;
  offset?: number;
}): Promise<PaginatedListResponse<SubscriptionListItem>> => {
  const params = new URLSearchParams();
  if (query?.status) params.set("status", query.status);
  if (query?.paymentSource) params.set("paymentSource", query.paymentSource);
  if (query?.brandId) params.set("brandId", query.brandId);
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.offset) params.set("offset", String(query.offset));

  const queryString = params.toString();
  const url = queryString ? `/admin/subscriptions?${queryString}` : `/admin/subscriptions`;
  const { data } = await apiClient.get<PaginatedListResponse<SubscriptionListItem>>(
    url
  );
  return data;
};

export const getAdminSubscriptionDetails = async (
  tenantId: string
): Promise<SubscriptionDetailsResponse> => {
  const { data } = await apiClient.get<SubscriptionDetailsResponse>(
    `/admin/subscriptions/${tenantId}`
  );
  return data;
};

export const getAdminSubscriptionHistory = async (
  tenantId: string,
  params?: { limit?: number; offset?: number }
): Promise<{
  items: StatusChangeHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}> => {
  const query = new URLSearchParams();
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));

  const queryString = query.toString();
  const url = queryString
    ? `/admin/subscriptions/${tenantId}/history?${queryString}`
    : `/admin/subscriptions/${tenantId}/history`;
  const { data } = await apiClient.get<{
    items: StatusChangeHistoryItem[];
    total: number;
    limit: number;
    offset: number;
  }>(url);
  return data;
};

export const suspendSubscription = async (
  tenantId: string,
  dto: SuspendSubscriptionDto
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/admin/subscriptions/${tenantId}/suspend`,
    dto
  );
  return data;
};

export const activateSubscription = async (
  tenantId: string,
  dto: ActivateSubscriptionDto
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/admin/subscriptions/${tenantId}/activate`,
    dto
  );
  return data;
};

export const extendSubscription = async (
  tenantId: string,
  dto: ExtendSubscriptionPeriodDto
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/admin/subscriptions/${tenantId}/extend`,
    dto
  );
  return data;
};

export const changeSubscriptionStatus = async (
  tenantId: string,
  dto: ChangeSubscriptionStatusDto
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/admin/subscriptions/${tenantId}/change-status`,
    dto
  );
  return data;
};

export const createManualSubscription = async (
  tenantId: string,
  dto: CreateManualSubscriptionDto
): Promise<CreateManualSubscriptionResponse> => {
  const { data } = await apiClient.post<CreateManualSubscriptionResponse>(
    `/admin/subscriptions/${tenantId}/create-manual`,
    dto
  );
  return data;
};

export const registerBankTransferPayment = async (
  dto: RegisterBankTransferPaymentDto
): Promise<RegisterBankTransferPaymentResponse> => {
  const { data } = await apiClient.post<RegisterBankTransferPaymentResponse>(
    "/admin/settlements/bank-transfer",
    dto
  );
  return data;
};

export const updateBillingPeriod = async (
  tenantId: string,
  dto: UpdateBillingPeriodDto
): Promise<UpdateBillingPeriodResponse> => {
  const { data } = await apiClient.post<UpdateBillingPeriodResponse>(
    `/admin/subscriptions/${tenantId}/billing-period`,
    dto
  );
  return data;
};

/**
 * Switch a Stripe-managed subscription to bank transfer billing.
 * Prerequisite for setting the billing period and migrating the tenant to Billing BC.
 * @see docs/specifications/2026-09-02-switch-stripe-subscription-to-bank-transfer/spec.md
 */
export const switchToBankTransfer = async (
  tenantId: string,
  dto: SwitchToBankTransferDto
): Promise<SwitchToBankTransferResponse> => {
  const { data } = await apiClient.post<SwitchToBankTransferResponse>(
    `/admin/subscriptions/${tenantId}/switch-to-bank-transfer`,
    dto
  );
  return data;
};

// =====================================================
// Billing Operations (Manual Billing Flow)
// =====================================================

export const createBillingOperation = async (
  tenantId: string,
  dto: CreateBillingOperationDto
): Promise<BillingOperationResponse> => {
  const { data } = await apiClient.post<BillingOperationResponse>(
    `/admin/subscriptions/${tenantId}/billing-operations`,
    dto
  );
  return data;
};

export const listBillingOperations = async (
  tenantId: string,
  params?: {
    dateFrom?: string;
    dateTo?: string;
    type?: "payment" | "extension" | "invoice";
    limit?: number;
    offset?: number;
  }
): Promise<PaginatedBillingOperationsResponse> => {
  const query = new URLSearchParams();
  if (params?.dateFrom) query.set("dateFrom", params.dateFrom);
  if (params?.dateTo) query.set("dateTo", params.dateTo);
  if (params?.type) query.set("type", params.type);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));

  const queryString = query.toString();
  const url = queryString
    ? `/admin/subscriptions/${tenantId}/billing-operations?${queryString}`
    : `/admin/subscriptions/${tenantId}/billing-operations`;
  const { data } = await apiClient.get<PaginatedBillingOperationsResponse>(url);
  return data;
};

export const sendBillingOperationInvoice = async (
  operationId: string,
  tenantId: string
): Promise<{ success: boolean }> => {
  const { data } = await apiClient.post<{ success: boolean }>(
    `/admin/subscriptions/billing-operations/${operationId}/send-invoice`,
    { tenantId }
  );
  return data;
};

// =====================================================
// Admin Proforma Generator
// =====================================================

/**
 * Create admin proforma for a tenant
 *
 * @param tenantId - UUID of the tenant
 * @param dto - Proforma creation parameters
 * @returns Created proforma details
 * @see docs/specifications/2026-02-18-admin-proforma-generator.md API Contract
 */
export const createAdminProforma = async (
  tenantId: string,
  dto: CreateAdminProformaDto
): Promise<CreateAdminProformaResponse> => {
  const { data } = await apiClient.post<CreateAdminProformaResponse>(
    `/admin/subscriptions/${tenantId}/proforma`,
    dto
  );
  return data;
};
